/* eslint-disable no-undef */
import React from 'react';
import qs from 'qs';

export const isBrowser = typeof window !== 'undefined';

export const debug = function debug(...args) {
  // If debug=1 is in the URL parameters
  if (typeof window !== 'undefined' && window?.location?.search?.includes('debug=1')) {
    // Console log using apply to pass the arguments
    // eslint-disable-next-line no-console
    console?.log?.apply(this, args);
  }
};

// Converts array of option sets into an object map
export const createOptionMap = (optionSets, language) => {
  let filteredOptionSets = optionSets;

  if (language !== undefined) {
    filteredOptionSets = optionSets.filter(
      (node) => node.language === language
    );
  }

  return filteredOptionSets.reduce((acc, optionSet) => {
    const newAcc = { ...acc };

    optionSet.options.forEach((option) => {
      let value;

      if (option.__typename === 'ContentfulOption') {
        value = option.value?.value
          ? option.value?.value : option.valueNoTranslate?.valueNoTranslate;
      } else if (option.__typename === 'ContentfulOptionJson') {
        let jsonData = option.value?.internal?.content;
        if (jsonData) {
          // Smartling is encoding & as &amp; in JSON data
          jsonData = jsonData.replace(/&amp;/g, '&');
          value = JSON.parse(jsonData);
        }
      }

      newAcc[option.key] = value;
    });
    return newAcc;
  }, {});
};

export const getFirst = (arr) => {
  if (arr !== null && Array.isArray(arr) && arr.length > 0) {
    return arr[0];
  }
  return null;
};

export const flattenArray = (array) => array.reduce((acc, curr) => acc.concat(curr), []);

// Needle must be a regex with a capture group
export const replaceWithComponent = (needle, haystack, Component, keyPrefix, parentIndex = 0) => {
  if (Array.isArray(haystack)) {
    return flattenArray(
      haystack.map(
        (item) => replaceWithComponent(needle, item, Component, keyPrefix, parentIndex + 1)
      )
    );
  }
  if (typeof haystack !== 'string') {
    return haystack;
  }

  return haystack.split(needle)
    .map((value, index) => {
      if (index % 2 !== 0) {
        // eslint-disable-next-line react/no-array-index-key
        return <Component key={`${keyPrefix}-${parentIndex}-${index}`} value={value} />;
      }
      return value;
    });
};

export const removeNoTranslateWrapper = (str) => {
  if (typeof str === 'string') {
    // We use {{{...}}} to mark text as a non-translatable placeholder in Smartling.
    // Smartling also uses <span translate="no">...</span> and <notranslate>...</notranslate>
    // to mark text as non-translatable
    return str
      .replace(/{{{/g, '').replace(/}}}/g, '')
      .replace(/<span translate="no">(.*?)<\/span>/gi, '$1')
      .replace(/<notranslate>(.*?)<\/notranslate>/gi, '$1');
  }

  return str;
};

export const outputIfNoVariables = (str) => {
  if (typeof str === 'string') {
    if (str.match(/{{(.*?)}}/g)) {
      return null;
    }

    return str;
  }

  return null;
};

export const objectDeepReplace = (obj, needle, replacement) => {
  if (typeof obj === 'object') {
    if (Array.isArray(obj)) {
      return obj.map((item) => objectDeepReplace(item, needle, replacement));
    }

    return Object.keys(obj).reduce((acc, key) => {
      const newAcc = { ...acc };
      newAcc[key] = objectDeepReplace(obj[key], needle, replacement);
      return newAcc;
    }, {});
  }

  if (typeof obj === 'string') {
    return obj.replace(needle, replacement);
  }

  return obj;
};

export const isVisitorBot = () => {
  if (!isBrowser) {
    return true;
  }

  const botRegex = /(googlebot|googleweblight|-google|google-|semrush|ahrefs|spiceworks|fuzz|wget|curl|python|httpunit|nutch|slurp|teoma|convera|prtg|go-http|bot|spider|crawl)/;
  return botRegex.test(window.navigator.userAgent.toLowerCase());
};

export const isVisitorMSIE = () => {
  if (!isBrowser) {
    return false;
  }

  return /msie/i.test(window.navigator.userAgent);
};

export const getBrowserLanguage = () => {
  if (!isBrowser) {
    return null;
  }

  return window.navigator.language;
};

export const sortQueryParams = (a, b) => {
  const moveParamsToEnd = ['gclid', 'msclkid', 'lqid'];

  if (moveParamsToEnd.includes(a)) {
    return 1;
  }

  if (moveParamsToEnd.includes(b)) {
    return -1;
  }

  return 0;
};

export const mergeQueryParams = (url, params) => {
  const urlParts = url.split('?');
  const existingParams = urlParts.length > 1
    ? qs.parse(urlParts[1]) : {};
  const newParams = { ...existingParams, ...params, platform: 'web' };
  const newSearchString = qs.stringify(newParams, { sort: sortQueryParams });
  return `${urlParts[0]}?${newSearchString}`;
};

export const detectReferrerInitials = () => {
  const searchEngines = ['google', 'bing', 'baidu', 'yahoo', 'duckduckgo', 'naver', 'ecosia.org', 'yandex', 'sogou', 'so.com', 'qwant.com', 'search.smt.docomo', 'go.mail.ru', 'ask', 'goo.ne', 'daum', 'rakuten', 'biglobe', 'seznam', 'aol', 'kvasir', '360.cn', 'onet', 'avg', 'centrum.cz', 'lycos', 'rambler', 'virgilio', 'auone', 'babylon', 'search-results', 'startsiden'];
  const { referrer } = document;
  const initials = {
    web_source: 'direct',
    web_medium: 'direct'
  };

  if (referrer.length > 0 && referrer.indexOf('splashtop.c') === -1) {
    searchEngines.forEach((source) => {
      if (referrer.indexOf(source) > -1) {
        initials.web_source = source;
        initials.web_medium = 'organic';
      }
    });

    if (initials.web_medium !== 'organic') {
      const referrerDomain = new URL(referrer).hostname;
      initials.web_source = referrerDomain;
      initials.web_medium = 'referral';
    }
  }

  return initials;
};

let intervalId;

const sendGAEvent = (category, action, label) => {
  // right now, there's only social media & form events
  if (isBrowser && typeof rudderanalytics !== 'undefined' && typeof rudderanalytics.track === 'function') {
    // if it's flexible form, then send 'Flexible form' string and return
    if (!!category && typeof category === 'string' && category === 'Flexible Form') {
      // window.dataLayer = window.dataLayer || [];
      rudderanalytics.track('Flexible_Form', {
        Form_Variation: action,
        Form_Event: label
      });
      console.log('Sending GA event: ', {
        event: 'Flexible_Form',
        Form_Variation: action,
        Form_Event: label
      });
      clearInterval(intervalId);
      return;
    }
    // data from social Media share looks like key value
    // obj { event: `social-share-click-${network}` }
    rudderanalytics.track('event', {
      event: category.event
    });
    console.log('Sending GA event: ', {
      event: category.event
    });
    // rudderanalytics.track(data);
    clearInterval(intervalId);
  } else {
    console.log('rudderanalytics not there yet:::');
  }
};

export const sendToGtm = (category, action = '', label = '') => {
  intervalId = setInterval(sendGAEvent(category, action, label), 300);
};

export const contentfulEditUrl = (entryId, compose = false) => {
  const spaceId = process.env.GATSBY_CONTENTFUL_SPACE_ID;
  const environment = process.env.GATSBY_CONTENTFUL_ENVIRONMENT;

  if (compose) {
    return `https://compose.contentful.com/spaces/${spaceId}/environments/${environment}/page/${entryId}`;
  }

  return `https://app.contentful.com/spaces/${spaceId}/environments/${environment}/entries/${entryId}`;
};

export const redirectIfEdit = (entryId, composeEditor = false) => {
  if (isBrowser && window.location.search.indexOf('edit') > -1) {
    const urlParams = new URLSearchParams(window.location.search);
    const editToken = urlParams.get('edit');

    if (editToken !== null) {
      const url = contentfulEditUrl(entryId, composeEditor);
      window.location.href = url;
    }
  }
};

export const setOsanoParam = (data, param, status) => {
  const denied = (status?.ANALYTICS === 'DENY');
  const docReferrerLength = document?.referrer?.length;
  const beenDisabled = (status?.beenDisabled === true);
  const disableParam = (beenDisabled === true
  && docReferrerLength < 1)
    ? 'true' : 'false';
  let paramString;
  if (param === 'page') {
    /* eslint-disable-next-line no-nested-ternary */
    paramString = denied ? 'disabled' : data.page;
  } else if (param === 'web_source') {
    paramString = (disableParam === 'true') ? 'disabled' : data.web_source;
  } else if (param === 'web_medium') {
    paramString = (disableParam === 'true') ? 'disabled' : data.web_medium;
  }

  return paramString;
};

// Recursive function to highlight search terms in rich text
export const recursiveHighlight = (block, searchRegex) => {
  if (block.nodeType === 'text') {
    const matches = block.value.match(searchRegex);

    if (matches !== null) {
      const wrappingText = block.value.split(searchRegex);

      const highlightedMatches = wrappingText.reduce((acc, match, index) => {
        const newData = [
          ...acc,
          {
            nodeType: 'text',
            value: match,
            data: block.data,
            marks: block.marks
          }
        ];

        if (index !== wrappingText.length - 1) {
          newData.push({
            nodeType: 'text',
            value: matches[index],
            data: block.data,
            marks: [
              ...block.marks,
              { type: 'highlight' }
            ]
          });
        }

        return newData;
      }, []);

      return highlightedMatches;
    }

    return block;
  }

  if (block.content) {
    return {
      ...block,
      content: block.content.reduce((acc, contentBlock) => {
        const highlightedBlock = recursiveHighlight(contentBlock, searchRegex);

        if (Array.isArray(highlightedBlock)) {
          return [...acc, ...highlightedBlock];
        }

        return [...acc, highlightedBlock];
      }, [])
    };
  }

  return block;
};
